import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
import BackButton from './BackButton';

const LockedPage = ({
  title, subtitle, lore, statusLabel = 'Access Restricted',
  tickerText = 'Awaiting signal...', theme, features = [],
  bottomCTA, bgImage, particles = 'embers',
}) => {
  const navigate = useNavigate();
  const [glitchText, setGlitchText] = useState(false);
  const { primary, glow, bg } = theme;

  useEffect(() => {
    const interval = setInterval(() => {
      setGlitchText(true);
      setTimeout(() => setGlitchText(false), 120);
    }, 5000 + Math.random() * 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen relative text-white overflow-x-hidden" data-testid="locked-page"
      style={{ background: bg }}>

      <style>{`
        @keyframes lockedShimmer { 0% { background-position: -200% center; } 100% { background-position: 200% center; } }
      `}</style>

      {/* Background image */}
      {bgImage && (
        <div className="absolute inset-0 z-[1] pointer-events-none">
          <img src={bgImage} alt="" className="w-full h-full object-cover opacity-15" style={{ mixBlendMode: 'screen' }} />
          <div className="absolute inset-0" style={{ background: `radial-gradient(ellipse at center, transparent 30%, ${bg} 80%)` }} />
        </div>
      )}

      {/* Ambient glow */}
      <div className="absolute inset-0 z-[1] pointer-events-none">
        <motion.div className="absolute inset-0"
          style={{ background: `radial-gradient(ellipse at 50% 40%, ${glow}18 0%, transparent 55%)` }}
          animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 4, repeat: Infinity }} />
      </div>

      {/* CRT scanlines */}
      <div className="fixed inset-0 z-[51] pointer-events-none mix-blend-overlay" style={{
        background: 'linear-gradient(rgba(18,16,16,0) 50%, rgba(0,0,0,0.2) 50%)',
        backgroundSize: '100% 4px', opacity: 0.12,
      }} />

      {/* Particles */}
      <div className="absolute inset-0 z-[2] pointer-events-none overflow-hidden">
        {particles === 'embers' && [...Array(10)].map((_, i) => (
          <motion.div key={`p-${i}`} className="absolute rounded-full"
            style={{
              left: `${3 + i * 10}%`, bottom: '-10px',
              width: `${2 + Math.random() * 3}px`, height: `${2 + Math.random() * 3}px`,
              background: primary, boxShadow: `0 0 ${6 + Math.random() * 6}px ${glow}cc`,
            }}
            animate={{ y: [0, -600], opacity: [0, 0.8, 0.3, 0] }}
            transition={{ duration: 5 + Math.random() * 3, delay: i * 0.4, repeat: Infinity, ease: 'easeOut' }}
          />
        ))}
        {particles === 'wisps' && [...Array(8)].map((_, i) => (
          <motion.div key={`w-${i}`} className="absolute rounded-full"
            style={{
              left: `${5 + i * 12}%`, bottom: '-20px',
              width: `${20 + Math.random() * 30}px`, height: `${20 + Math.random() * 30}px`,
              background: `radial-gradient(circle, ${glow}25 0%, transparent 70%)`, filter: 'blur(4px)',
            }}
            animate={{ y: [0, -550], opacity: [0, 0.5, 0.2, 0], scale: [0.5, 1.2, 0.6] }}
            transition={{ duration: 8 + Math.random() * 4, delay: i * 1.2, repeat: Infinity, ease: 'easeOut' }}
          />
        ))}
        {particles === 'orbs' && [...Array(8)].map((_, i) => (
          <motion.div key={`o-${i}`} className="absolute rounded-full"
            style={{
              left: `${6 + i * 12}%`, bottom: '-8px',
              width: '5px', height: '5px',
              background: primary, boxShadow: `0 0 12px ${glow}aa, 0 0 25px ${glow}40`,
            }}
            animate={{ y: [0, -500], opacity: [0, 1, 0.4, 0] }}
            transition={{ duration: 5 + Math.random() * 3, delay: i * 1 + 0.5, repeat: Infinity, ease: 'easeOut' }}
          />
        ))}
      </div>

      <BackButton to="/" />

      {/* Content — scrollable, centered */}
      <div className="relative z-10 flex flex-col items-center px-4 sm:px-6 pt-20 pb-12 max-w-2xl mx-auto min-h-screen justify-center">

        {/* Status badge */}
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          className="flex items-center gap-2 px-4 py-1.5 mb-4"
          style={{ background: `${glow}12`, border: `1px solid ${glow}35` }}
          data-testid="locked-badge">
          <motion.div className="w-2 h-2 rounded-full" style={{ background: primary, boxShadow: `0 0 8px ${glow}` }}
            animate={{ opacity: [1, 0.3, 1] }} transition={{ duration: 2, repeat: Infinity }} />
          <span className="text-[10px] tracking-[0.2em] uppercase font-bold" style={{ fontFamily: "'Cinzel', serif", color: primary }}>
            {statusLabel}
          </span>
        </motion.div>

        {/* Title */}
        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5, type: 'spring', stiffness: 100 }}
          className="relative text-3xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-wider text-center mb-3"
          style={{ fontFamily: "'Cinzel Decorative', 'Cinzel', serif" }}
          data-testid="locked-title">
          <AnimatePresence>
            {glitchText && (
              <>
                <motion.span initial={{ opacity: 0 }} animate={{ opacity: 0.4 }} exit={{ opacity: 0 }}
                  className="absolute inset-0" style={{ color: primary, transform: 'translate(-3px, -1px)', filter: 'blur(1px)' }}>
                  {title}
                </motion.span>
                <motion.span initial={{ opacity: 0 }} animate={{ opacity: 0.25 }} exit={{ opacity: 0 }}
                  className="absolute inset-0" style={{ color: '#06b6d4', transform: 'translate(3px, 1px)', filter: 'blur(1px)' }}>
                  {title}
                </motion.span>
              </>
            )}
          </AnimatePresence>
          <span className="absolute inset-0" style={{ color: 'transparent', WebkitTextStroke: `2px ${glow}30`, filter: 'blur(6px)' }}>
            {title}
          </span>
          <span className="relative" style={{
            background: `linear-gradient(180deg, ${primary} 0%, ${glow} 50%, ${primary}aa 100%)`,
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            filter: `drop-shadow(0 0 25px ${glow}80) drop-shadow(0 0 50px ${glow}30)`,
          }}>{title}</span>
          <motion.span className="absolute inset-0 pointer-events-none"
            style={{ background: `linear-gradient(90deg, transparent 0%, ${primary}60 50%, transparent 100%)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundSize: '200% 100%' }}
            animate={{ backgroundPosition: ['200% 0%', '-200% 0%'] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}>
            {title}
          </motion.span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
          className="text-sm sm:text-base text-center max-w-md mb-2 leading-relaxed"
          style={{ fontFamily: "'Cinzel', serif", color: `${primary}cc` }}>
          {subtitle}
        </motion.p>

        {/* Lore */}
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}
          className="text-xs sm:text-sm text-center max-w-lg mb-5 leading-relaxed px-2"
          style={{ fontFamily: "'Courier New', monospace", color: `${glow}70` }}>
          {lore}
        </motion.p>

        {/* Ticker */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }}
          className="flex items-center gap-2 px-3 py-1.5 mb-6"
          style={{ background: `${glow}08`, border: `1px solid ${glow}18` }}>
          <motion.div className="w-1.5 h-1.5 rounded-full" style={{ background: primary }}
            animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1.5, repeat: Infinity }} />
          <motion.span className="text-[9px] sm:text-[10px] tracking-[0.15em] uppercase font-bold"
            style={{ fontFamily: "'Courier New', monospace", color: `${primary}80` }}
            animate={{ opacity: [0.4, 0.8, 0.4] }} transition={{ duration: 3, repeat: Infinity }}>
            {tickerText}
          </motion.span>
        </motion.div>

        {/* Feature cards — 1 col on mobile, 2 on sm+ */}
        <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 mb-6">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div key={f.title} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1 + i * 0.1 }}
                className="relative p-3 sm:p-4 overflow-hidden"
                style={{ background: `${glow}08`, border: `1px solid ${glow}20` }}
                data-testid={`locked-feature-${i}`}>
                <div className="absolute top-0 left-0 w-2 h-2 border-t border-l" style={{ borderColor: `${glow}40` }} />
                <div className="absolute top-0 right-0 w-2 h-2 border-t border-r" style={{ borderColor: `${glow}40` }} />
                <div className="absolute bottom-0 left-0 w-2 h-2 border-b border-l" style={{ borderColor: `${glow}40` }} />
                <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r" style={{ borderColor: `${glow}40` }} />
                <div className="flex items-start gap-3">
                  <div className="p-1.5 sm:p-2 flex-shrink-0" style={{ background: `${glow}12`, border: `1px solid ${glow}25` }}>
                    <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" style={{ color: primary, filter: `drop-shadow(0 0 4px ${glow}60)` }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs sm:text-sm font-bold truncate" style={{ fontFamily: "'Cinzel', serif", color: `${primary}dd` }}>{f.title}</p>
                    <p className="text-[10px] sm:text-[11px] leading-relaxed mt-0.5" style={{ fontFamily: "'Courier New', monospace", color: `${glow}55` }}>{f.desc}</p>
                  </div>
                  <Lock className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0 mt-1" style={{ color: `${glow}35` }} />
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        {bottomCTA && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5 }}
            className="w-full">
            {bottomCTA}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default LockedPage;
